import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { useSubscribed } from '@/hooks/user';
import { SUBSCRIPTION_STATUS } from '@/models/subscription';
import { getPriceById } from '@/utils/price';

function getMessageKey({ hasDispute, status, handlerPriceId }, prices) {
	const currentPrice = getPriceById(handlerPriceId, prices);

	const hasPayAction =
		status === SUBSCRIPTION_STATUS.PAST_DUE &&
		!!(currentPrice?.active || !handlerPriceId);

	if (hasDispute) {
		return 'billing.subscription.disputedPaymentMessage';
	}

	if (status === SUBSCRIPTION_STATUS.EXPIRED) {
		return 'billing.subscription.incompleteExpired';
	}

	if (status === SUBSCRIPTION_STATUS.PENDING) {
		return 'billing.subscription.failedPaymentMessage';
	}

	if (status === SUBSCRIPTION_STATUS.CANCELED) {
		return 'billing.subscription.canceledSubscription';
	}

	if (hasPayAction) {
		return 'billing.subscription.failedPaymentMessage';
	}
}

function Description({ subscriptionInfo }) {
	const { t } = useTranslation();
	const subscribed = useSubscribed();
	const prices = useSelector(state => state.checkout?.prices || []);
	const {
		cancellationRequestAt: willCancel,
		currentPeriodEnd,
		subscriptionEnded,
		status,
		trialingEndsAt
	} = subscriptionInfo;

	const messageKey = getMessageKey(subscriptionInfo, prices);

	const periodEnd =
		status === SUBSCRIPTION_STATUS.TRIALING
			? trialingEndsAt
			: currentPeriodEnd;

	const periodEndDate = dayjs.unix(periodEnd).format('LL');
	const subscriptionEndedDate = dayjs.unix(subscriptionEnded).format('LL');

	if (messageKey) {
		return <p className="text-xs leading-5 text-grey-500">{t(messageKey)}</p>;
	}

	if (subscribed && willCancel) {
		return (
			<>
				<h3 className="text-xs font-semibold leading-5 text-grey-500">
					{t('billing.subscription.willCancel')}
				</h3>

				<span className="text-xs leading-5 text-grey-500">
					{periodEndDate}
				</span>

				{subscriptionEnded && (
					<>
						<h3 className="text-xs font-semibold leading-5 text-grey-500">
							{t('billing.subscription.canceledAt')}
						</h3>

						<span className="text-xs leading-5 text-grey-500">
							{subscriptionEndedDate}
						</span>
					</>
				)}
			</>
		);
	}

	if (subscribed && !willCancel) {
		return (
			<>
				<h3 className="text-xs font-semibold leading-5 text-grey-500">
					{t('billing.subscription.nextRenewal')}
				</h3>
				<span className="text-xs leading-5 text-grey-500">
					{periodEndDate}
				</span>
			</>
		);
	}

	return null;
}

export default Description;
